import { inject, Injectable } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { map, type Observable, switchMap, take } from 'rxjs';

import { filterBoolean } from '@core/common-legacy';
import { I18nService } from '@core/i18n-legacy';
import { ORG_GUID } from '@core/tellent-types';
import { injectTellentAppUrlBuilder } from '@core/tellent-util';
import { ClipboardCopy, DialogService } from '@core/user-interface-legacy';
import { AskTemplatesActions } from '@tellent-ask-hr/templates-data';

import { AskTemplatesDataService } from './ask-templates-data.service';

@Injectable({ providedIn: 'root' })
export class AskTemplatesService {
  private readonly dialogService = inject(DialogService);
  private readonly templatesDataService = inject(AskTemplatesDataService);
  private readonly i18n = inject(I18nService);
  private readonly actions$ = inject(Actions);
  private readonly clipboardCopy = inject(ClipboardCopy);
  private readonly tellentAppUrlBuilder = injectTellentAppUrlBuilder();
  private readonly orgGuid = inject(ORG_GUID);

  public getViewRefreshEvents$(): Observable<void> {
    return this.actions$.pipe(
      ofType(
        AskTemplatesActions.createOnePessimisticSuccess,
        AskTemplatesActions.updateOnePessimisticSuccess,
        AskTemplatesActions.removeOnePessimisticSuccess,
        AskTemplatesActions.duplicateTemplateSuccess,
      ),
      map(() => void 0),
    );
  }

  public confirmDelete(templateId: number): Observable<boolean> {
    return this.dialogService
      .error({
        title: this.i18n.translate('tlc.ask_hr.forms.delete_form_heading'),
        message: this.i18n.translate('tlc.ask_hr.forms.delete_form_description'),
      })
      .pipe(
        take(1),
        filterBoolean,
        switchMap(isSuccess =>
          isSuccess
            ? this.templatesDataService
                .deleteTemplate(templateId)
                .pipe(map(result => result.isSuccess))
            : [false],
        ),
      );
  }

  public copyTemplateLink(templateId: number) {
    const tellentUrl = this.tellentAppUrlBuilder(
      'core_hr',
      {
        id: 'ask:new_ticket',
        params: {
          template_id: templateId,
        },
      },
      this.orgGuid!,
    );
    this.clipboardCopy.fromString(tellentUrl);
  }

  public duplicateTemplate(templateId: number): Observable<number | null> {
    return this.templatesDataService.loadTemplateById(templateId).pipe(
      switchMap(({ isSuccess, success }) => {
        if (isSuccess) {
          const { id: _1, createdAt: _2, updatedAt: _3, autoAssignees, ...template } = success!;
          return this.templatesDataService
            .duplicateTemplate({
              ...template,
              active: false,
              name: `${template.name} (${this.i18n.translate('tlc.ask_hr.forms.duplicate_added_suffix')})`,
              autoAssignments: autoAssignees.map(assignee => ({ memberGuid: assignee.guid })),
            })
            .pipe(map(result => (result.isSuccess ? result.success!.id : null)));
        } else {
          return [null];
        }
      }),
    );
  }
}
